import React, { useState, useEffect } from 'react';
import styles from './SuccessStep.module.css';
import { axisBranding } from './branding';

interface SuccessStepProps {
  onRestart: () => void;
}

const SuccessStep: React.FC<SuccessStepProps> = React.memo(({ onRestart }) => {
  const [isMobile, setIsMobile] = useState(false);

  const checkMobile = React.useCallback(() => {
    setIsMobile(window.innerWidth <= 700);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  // Navigation links for mobile responsiveness
  const NAV_PHONE = axisBranding.phoneNumber;
  const NAV_LINKS = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  return (
  <div className={styles.success}>
    {/* Header/Navigation */}
    <div className={styles.navBar}>
      <span className={styles.navPhone}>{NAV_PHONE}</span>
      <div className={styles.navLinks}>
        {currentNavLinks.map(link => (
          <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
        ))}
      </div>
      <div className={styles.navMenuIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>

    {/* Main Content */}
    <div className={styles.mainContent}>
      <div className={styles.confirmationCard}>
        <div className={styles.successIcon}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#10b981" strokeWidth="2"/>
            <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <h1 className={styles.title}>Appointment Confirmed!</h1>
        <p className={styles.subtitle}>
          Your appointment has been successfully booked. You will receive a confirmation email shortly with all the details.
        </p>
        
        <div className={styles.confirmationDetails}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>What's Next:</span>
            <span className={styles.detailValue}>
              Check your email for appointment details and any pre-appointment instructions.
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Reminder:</span>
            <span className={styles.detailValue}>
              You'll receive a reminder 24 hours before your appointment.
            </span>
          </div>
        </div>

        <div className={styles.actionButtons}>
          <button
            className={styles.primaryButton}
            onClick={onRestart}
          >
            Book Another Appointment
          </button>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className={styles.footer}>
      <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
      <div className={styles.footerInfo}>
        <div className={styles.footerInfoItem}>
          <span>📞</span>
          <span>Phone (appointments): {axisBranding.phoneNumber}</span>
        </div>
        <div className={styles.footerInfoItem}>
          <span>📍</span>
        </div>
      </div>
      <div className={styles.rating}>
        <span className={styles.star}>★</span>
        <span className={styles.star}>★</span>
        <span className={styles.star}>★</span>
        <span className={styles.star}>★</span>
        <span className={styles.star}>★</span>
      </div>
      <div className={styles.ratingText}>Average Rating: 4.9/5</div>
    </div>

    {/* Accessibility Icon */}
    <div className={styles.accessibilityIcon}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="4" r="2" fill="white"/>
        <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </div>
  </div>
  );
});

export default SuccessStep; 